import { useGT } from 'gt-react';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { Separator } from '@/components/ui/separator';
import { WarningIcon } from '@phosphor-icons/react';
import {
  TroubleType,
  type TroubleData,
  type TroubleEntry,
} from '@erp/shared-types';
import {
  countHoldSources,
  type HoldOrigin,
  type HoldSource,
} from '@/components/common/holdSources';
import { cn } from '@/lib/utils';
import { getActiveTroubleEntries } from '@/components/common/troubleEntries';
import {
  orderNotReadyPausedMessage,
  visibleTroubleComments,
} from '@/components/common/orderNotReadyComment';

const TROUBLE_STYLES: Record<
  string,
  { className: string; iconClassName: string }
> = {
  [TroubleType.ON_HOLD]: {
    className: 'text-amber-700 border-amber-300 bg-amber-50',
    iconClassName: 'text-amber-600',
  },
  [TroubleType.MISMATCH]: {
    className: 'text-orange-700 border-orange-300 bg-orange-50',
    iconClassName: 'text-orange-600',
  },
  [TroubleType.ERROR]: {
    className: 'text-red-700 border-red-300 bg-red-50',
    iconClassName: 'text-red-600',
  },
  [TroubleType.ORDER_NOT_READY]: {
    className: 'text-blue-700 border-blue-300 bg-blue-50',
    iconClassName: 'text-blue-600',
  },
  [TroubleType.EXPECTED_COST_MISSING]: {
    className: 'text-violet-700 border-violet-300 bg-violet-50',
    iconClassName: 'text-violet-600',
  },
  [TroubleType.LINE_TOTAL_MISMATCH]: {
    className: 'text-red-700 border-red-300 bg-red-50',
    iconClassName: 'text-red-600',
  },
  [TroubleType.ORDER_LESS_LOAD_FREIGHT]: {
    className: 'text-amber-700 border-amber-300 bg-amber-50',
    iconClassName: 'text-amber-600',
  },
};

function getEffectiveEntries(
  troubleData: TroubleData | null | undefined,
  legacyHoldPay?: boolean,
  legacyConsign?: boolean,
) {
  const entries = getActiveTroubleEntries(troubleData);
  if (legacyHoldPay && !entries.some((e) => e.value === 'HOLD_PAY')) {
    entries.push({
      active: true,
      type: TroubleType.ON_HOLD,
      source: 'famous',
      value: 'HOLD_PAY',
    });
  }
  if (legacyConsign && !entries.some((e) => e.value === 'CONSIGN')) {
    entries.push({
      active: true,
      type: TroubleType.ON_HOLD,
      source: 'famous',
      value: 'CONSIGN',
    });
  }
  return entries;
}

function getStyle(type: string) {
  return (
    TROUBLE_STYLES[type] ?? {
      className: 'text-gray-700 border-gray-300 bg-gray-50',
      iconClassName: 'text-gray-600',
    }
  );
}

function troubleEntryKey(entry: TroubleEntry): string {
  return JSON.stringify([
    entry.type,
    entry.source,
    entry.value,
    entry.comments,
  ]);
}

interface TroubleDataBadgesProps {
  troubleData: TroubleData | null | undefined;
  /** The record the trouble entries came from; shown as the hold's source. */
  origin?: HoldOrigin | null;
  legacyHoldPay?: boolean;
  legacyConsign?: boolean;
  className?: string;
  size?: 'sm' | 'md';
}

export function TroubleDataBadges({
  troubleData,
  origin,
  legacyHoldPay,
  legacyConsign,
  className,
  size = 'sm',
}: TroubleDataBadgesProps) {
  const gt = useGT();
  const entries = getEffectiveEntries(
    troubleData,
    legacyHoldPay,
    legacyConsign,
  );
  if (entries.length === 0) return null;

  // `md` keeps the base Badge sizing so it lines up with the status and
  // payment badges it sits beside in detail headers.
  const sizeClasses = size === 'sm' ? 'text-[10px] px-1 py-0 h-4' : 'text-xs';

  return (
    <div className={cn('inline-flex items-center gap-0.5', className)}>
      {entries.map((entry, i) => {
        const style = getStyle(entry.type);
        const orderNotReady = entry.type === TroubleType.ORDER_NOT_READY;

        return (
          <Tooltip key={`${entry.type}-${i}`}>
            <TooltipTrigger asChild>
              <Badge
                variant="outline"
                className={cn(style.className, sizeClasses, 'shrink-0')}
              >
                {entryLabel(entry, gt)}
              </Badge>
            </TooltipTrigger>
            <TooltipContent
              sideOffset={4}
              className="max-w-xs text-xs space-y-0.5"
            >
              <p className="font-medium">{entryLabel(entry, gt)}</p>
              {orderNotReady && <p>{orderNotReadyPausedMessage(gt)}</p>}
              {!orderNotReady && entry.value && (
                <p className="text-muted-foreground font-mono">{entry.value}</p>
              )}
              {!orderNotReady && entry.source && (
                <p className="text-muted-foreground">
                  {gt('Source')}: {sourceLabel(entry.source, origin, gt)}
                </p>
              )}
              {visibleTroubleComments(entry, gt).map((line, ci) => (
                <p key={ci}>{line}</p>
              ))}
            </TooltipContent>
          </Tooltip>
        );
      })}
    </div>
  );
}

interface TroubleDataIconProps {
  troubleData: TroubleData | null | undefined;
  className?: string;
}

export function TroubleDataIcon({
  troubleData,
  className,
}: TroubleDataIconProps) {
  const gt = useGT();
  const entries = getActiveTroubleEntries(troubleData);
  if (entries.length === 0) return null;

  const labels = entries.map((e) => translateLabel(e.type, gt)).join(', ');

  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <span
          className={cn(
            'inline-flex items-center text-amber-600 shrink-0',
            className,
          )}
        >
          <WarningIcon className="h-3.5 w-3.5" />
        </span>
      </TooltipTrigger>
      <TooltipContent className="text-xs">{labels}</TooltipContent>
    </Tooltip>
  );
}

interface TroubleDataAlertProps {
  onHoldCount: number;
  /** Every active hold behind `onHoldCount`, one entry per record. */
  holdSources?: readonly HoldSource[];
  /** The hold lookup failed, so no holds is not a known answer. */
  checkFailed?: boolean;
  className?: string;
}

export function TroubleDataAlert({
  onHoldCount,
  holdSources = [],
  checkFailed = false,
  className,
}: TroubleDataAlertProps) {
  const gt = useGT();
  if (checkFailed) {
    return (
      <div
        role="alert"
        className={cn(
          'rounded-md border border-red-300 bg-red-50 p-3 text-sm text-red-800 flex items-start gap-2',
          className,
        )}
      >
        <WarningIcon className="h-4 w-4 shrink-0 mt-0.5" />
        <p>
          {gt(
            'Could not check its POs, orders or loads for holds. Check them in the source ERP before paying.',
            {
              context:
                'invoice hold alert when the source-ERP hold lookup failed',
            },
          )}
        </p>
      </div>
    );
  }
  if (onHoldCount === 0 && holdSources.length === 0) return null;

  return (
    <div
      className={cn(
        'rounded-md border border-amber-300 bg-amber-50 p-3 text-sm text-amber-800 flex items-start gap-2',
        className,
      )}
    >
      <WarningIcon className="h-4 w-4 shrink-0 mt-0.5" />
      <div className="space-y-1">
        <p>
          {onHoldCount > 0
            ? gt('{count} line(s) on its POs, orders or loads are on hold.', {
                context:
                  'invoice hold alert; holds on documents linked to the invoice',
                count: String(onHoldCount),
              })
            : gt(
                'Its POs, orders or loads carry holds that do not block payment.',
                {
                  context:
                    'invoice hold alert when only informational holds (e.g. a Famous load hold) are present',
                },
              )}
        </p>
        <HoldSourceList holdSources={holdSources} />
      </div>
    </div>
  );
}

/** One row per hold kind and record kind, with document references. */
function HoldSourceList({
  holdSources,
}: {
  holdSources: readonly HoldSource[];
}) {
  const gt = useGT();
  if (holdSources.length === 0) return null;
  return (
    <ul className="list-disc pl-4 text-xs">
      {countHoldSources(holdSources).map(
        ({ value, origin, count, references }) => (
          <li key={`${value}-${origin}`}>
            {gt('{hold} — {origin} ({count})', {
              context:
                'one row of the invoice hold alert, e.g. "Hold Pay — Sales load charge (2)"',
              hold: holdValueLabel(value, gt) ?? value,
              count: String(count),
              origin: holdOriginLabel(origin, gt),
            })}
            {references.length > 0 && `: ${references.join(', ')}`}
          </li>
        ),
      )}
    </ul>
  );
}

/**
 * List-row warning icon for everything holding up an invoice: our payment
 * hold, the invoice's own trouble entries, and source-ERP holds on its linked
 * documents. Icon only; the popover explains each one.
 */
export function InvoiceHoldsIcon({
  paymentHold,
  paymentHoldReason,
  troubleData,
  holdSources,
  className,
}: {
  paymentHold: boolean;
  paymentHoldReason?: string | null;
  troubleData: TroubleData | null | undefined;
  holdSources: readonly HoldSource[];
  className?: string;
}) {
  const gt = useGT();
  const invoiceTrouble = Array.from(
    new Map(
      getActiveTroubleEntries(troubleData).map(
        (entry) => [troubleEntryKey(entry), entry] as const,
      ),
    ).values(),
  );
  if (!paymentHold && invoiceTrouble.length === 0 && holdSources.length === 0) {
    return null;
  }
  const label = gt('Holds and issues', {
    context: 'accessible label of the invoice list warning icon',
  });

  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="outline"
          size="icon"
          aria-label={label}
          onClick={(event) => event.stopPropagation()}
          className={cn(
            'h-5 w-5 justify-center p-0 text-amber-700 border-amber-300 bg-amber-50 hover:bg-amber-100 shrink-0',
            className,
          )}
        >
          <WarningIcon className="h-3 w-3" aria-hidden />
        </Button>
      </PopoverTrigger>
      <PopoverContent
        align="start"
        className="w-80 max-w-[calc(100vw-2rem)] max-h-80 overflow-y-auto p-3 text-sm flex flex-col gap-3"
        aria-label={label}
        onClick={(event) => event.stopPropagation()}
      >
        <p className="font-semibold">{label}</p>
        <Separator />
        {paymentHold && (
          <div>
            <p className="font-medium">
              {gt('Payment on hold', {
                context: 'invoice list warning details: our own payment hold',
              })}
            </p>
            {paymentHoldReason && <p>{paymentHoldReason}</p>}
          </div>
        )}
        {invoiceTrouble.length > 0 && (
          <ul className="flex flex-col gap-3">
            {invoiceTrouble.map((entry) => (
              <li key={troubleEntryKey(entry)} className="flex flex-col gap-1">
                <p className="font-medium">{entryLabel(entry, gt)}</p>
                {entry.type === TroubleType.ORDER_NOT_READY && (
                  <p className="text-xs text-muted-foreground">
                    {orderNotReadyPausedMessage(gt)}
                  </p>
                )}
                {entry.source && (
                  <p className="text-xs text-muted-foreground">
                    {gt('Source: {source}', {
                      context: 'source system of an invoice warning',
                      source: sourceLabel(entry.source, undefined, gt),
                    })}
                  </p>
                )}
                {Array.from(new Set(visibleTroubleComments(entry, gt))).map(
                  (comment) => (
                    <p
                      key={comment}
                      className="text-xs text-muted-foreground break-words"
                    >
                      {comment}
                    </p>
                  ),
                )}
              </li>
            ))}
          </ul>
        )}
        {holdSources.length > 0 && (
          <div>
            <p className="font-medium">
              {gt('Holds on its POs, orders or loads', {
                context:
                  'popover heading for linked-document holds on an invoice',
              })}
            </p>
            <HoldSourceList holdSources={holdSources} />
          </div>
        )}
      </PopoverContent>
    </Popover>
  );
}

function holdValueLabel(
  value: string | undefined,
  gt: ReturnType<typeof useGT>,
): string | null {
  switch (value) {
    case 'HOLD_PAY':
      return gt('Hold Pay', {
        context: 'Famous "Hold Pay" flag on a PO line or sales load charge',
      });
    case 'LOAD_HOLD':
      return gt('Load Hold', {
        context: 'the whole sales load is on hold in Famous',
      });
    case 'CONSIGN':
      return gt('Consignment', {
        context: 'sales order line is on consignment in Famous',
      });
    default:
      return null;
  }
}

function holdOriginLabel(
  origin: HoldOrigin,
  gt: ReturnType<typeof useGT>,
): string {
  switch (origin) {
    case 'PURCHASE_ORDER_LINE':
      return gt('PO line', { context: 'source record of a hold' });
    case 'SALES_ORDER_LINE':
      return gt('Sales order line', { context: 'source record of a hold' });
    case 'SALES_LOAD_CHARGE':
      return gt('Sales load charge', {
        context:
          'source record of a hold; a charge in Famous Sales Load Management',
      });
    case 'CHARGE':
      return gt('Charge', { context: 'source record of a hold' });
  }
}

function sourceLabel(
  source: string,
  origin: HoldOrigin | null | undefined,
  gt: ReturnType<typeof useGT>,
): string {
  const system = source === 'famous' ? 'Famous' : source;
  return origin ? `${system} · ${holdOriginLabel(origin, gt)}` : system;
}

function entryLabel(
  entry: { type: string; value?: string },
  gt: ReturnType<typeof useGT>,
): string {
  const holdLabel =
    entry.type === TroubleType.ON_HOLD ? holdValueLabel(entry.value, gt) : null;
  return holdLabel ?? translateLabel(entry.type, gt);
}

function translateLabel(type: string, gt: ReturnType<typeof useGT>): string {
  switch (type) {
    case TroubleType.ON_HOLD:
      return gt('On Hold');
    case TroubleType.MISMATCH:
      return gt('Mismatch');
    case TroubleType.ERROR:
      return gt('Error');
    case TroubleType.ORDER_NOT_READY:
      return gt('Order Not Ready');
    case TroubleType.EXPECTED_COST_MISSING:
      return gt('Missing Expected Freight Cost');
    case TroubleType.LINE_TOTAL_MISMATCH:
      return gt('Total Mismatch', {
        context:
          'badge when AP invoice line items do not add up to the document total',
      });
    case TroubleType.ORDER_LESS_LOAD_FREIGHT:
      return gt('Freight on Load With No Orders', {
        context:
          'badge when an AP invoice cites a load that carries freight but has no orders',
      });
    default:
      return type;
  }
}

export interface OnHoldSummary {
  onHoldCount: number;
  holdSources: HoldSource[];
}
